'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';

type Lang = 'en' | 'es';

const translations = {
  en: {
    // Topbar
    brandSub: 'Gold Signal Platform',
    pairLabel: 'Pullback',
    live: 'LIVE',
    secure: 'SECURE',
    proMember: 'Pro Member',
    howItWorks: 'How It Works',
    howItWorksSub: 'Signal guide & manual trading',
    // How It Works Modal
    modalTitle: 'How the Signal Generator Works',
    modalSub: 'XAU ENGINE AI PRO — Trader Guide',
    section1Title: 'How the Signal Generator Works',
    section1Intro: 'The XAU Engine AI signal generator continuously analyzes the XAU/USD (Gold) market using a multi-layer scoring system. Each signal is built from the following components:',
    trendLabel: 'Trend Analysis',
    trendDesc: 'Detects the dominant market direction using moving averages and price structure on the M15 timeframe.',
    momentumLabel: 'Momentum Score',
    momentumDesc: 'Measures the strength of the current move using RSI, MACD divergence, and volume pressure.',
    entryLabel: 'Entry Zone Detection',
    entryDesc: 'Identifies high-probability entry zones based on support/resistance levels, Fibonacci retracements, and order blocks.',
    sessionLabel: 'Session Timing',
    sessionDesc: 'Filters signals by active trading sessions (London, New York) to avoid low-liquidity periods.',
    riskLabel: 'Risk Filter',
    riskDesc: 'Suppresses signals during high-impact news events and abnormal spread conditions to protect your capital.',
    section2Title: 'Signal Types',
    buyDesc: 'Bullish setup detected. Price expected to move upward.',
    sellDesc: 'Bearish setup detected. Price expected to move downward.',
    waitDesc: 'No clear setup. Avoid trading until conditions improve.',
    section3Title: 'How to Take Manual Action on a Signal',
    section3Intro: 'The signal generator does',
    section3IntroNot: 'not',
    section3IntroEnd: 'place trades automatically. You are always in control. Follow these steps when a signal appears:',
    step1Title: 'Wait for Signal Confirmation',
    step1Desc: 'A signal is only actionable when the Score Ring reaches ≥ 70%. Below that, the engine is still building confluence — do not enter early.',
    step2Title: 'Check the Entry Zone',
    step2Desc: 'Look at the Entry Zone panel on the signal card. Your ideal entry price should be within the displayed range. Do not chase price if it has already moved far beyond the zone.',
    step3Title: 'Set Your Stop Loss',
    step3Desc: 'Use the Stop Loss level shown on the signal card. Place your SL a few pips beyond that level to account for spread. Never skip the stop loss.',
    step4Title: 'Set Your Take Profit Targets',
    step4Desc: 'The signal provides TP1, TP2, and TP3 targets. Consider closing 50% of your position at TP1 and letting the rest run to TP2/TP3 to maximize reward.',
    step5Title: 'Monitor & Manage',
    step5Desc: 'Once in a trade, watch the signal score. If the score drops sharply or a new opposing signal appears, consider closing early to protect profits.',
    step6Title: 'Log Your Trade',
    step6Desc: "After closing, review the Signal History section to compare your entry/exit with the signal's predicted levels. This helps you improve over time.",
    disclaimer: 'Risk Disclaimer: ',
    disclaimerText: 'Trading gold (XAU/USD) involves significant risk. Signals are analytical tools, not financial advice. Always use proper risk management and never risk more than you can afford to lose.',
    // Session bar
    bestWindow: 'Best window:',
    volatility: 'Volatility:',
    overlap: 'OVERLAP',
    // Footer
    footerInfo: 'For informational purposes only. Not financial advice. Always manage your own risk.',
    footerVersion: 'XAU Engine AI Pro v2.4.1 — Build 20260325',
    footerPair: 'XAU/USD · M15 · Pullback Strategy',
    // Signal Generator section
    signalGenerator: 'Signal Generator',
    signalGeneratorSub: 'AI-powered XAU/USD M15 pullback analysis',
    generateSignal: 'Generate Signal',
    generating: 'Generating...',
    refresh: 'Refresh',
    refreshSignal: 'Refresh Signal',
    getSignal: 'GET SIGNAL',
    getNewSignal: 'GET NEW SIGNAL',
    tryAgain: 'TRY AGAIN',
    signalRequestError: 'Make sure your Python background script is running and connected to Supabase.',
    // Signal history
    signalHistory: 'Signal History',
    signalHistorySub: 'signals',
    totalSignals: 'Total Signals',
    avgAiScore: 'Avg AI Score',
    direction: 'Direction:',
    signalId: 'Signal ID',
    directionCol: 'Direction',
    entryZone: 'Entry Zone',
    status: 'Status',
    aiScore: 'AI Score',
    timestamp: 'Timestamp',
    noSignalsYet: 'No signals stored yet',
    noSignalsMatch: 'No signals match this filter',
    noSignalsHint: 'POST to /api/signals from your bot to store signals',
    adjustFilters: 'Adjust your filters to see signal history',
    showing: 'Showing',
    of: 'of',
    // Signal empty state
    noSignalYet: 'No signal generated yet',
    noSignalDesc: 'Press',
    noSignalDescMid: 'to run the AI pullback analysis on XAU/USD M15. The engine scans structure, momentum, and session context to deliver a precision trade setup.',
    noSignalRecommended: 'Recommended: Only take signals above 70% in both AI Score and Confidence.',
    featureEntryLabel: 'Precision Entry Zones',
    featureEntryDesc: 'Best + OK ranges with no-chase price',
    featureTpLabel: 'TP1–TP5 Ladder',
    featureTpDesc: 'Pip values + probability for each target',
    featureSlLabel: 'Dual Stop Loss',
    featureSlDesc: 'Trade SL and hard SL levels',
    featureTimingLabel: 'Timing Intelligence',
    featureTimingDesc: 'Best entry, enter-by, and valid-until times',
    // Signal card labels
    priceAtSignal: 'Price at signal',
    bestEntry: 'Best Entry',
    okEntry: 'OK Entry',
    ideal: 'IDEAL',
    ok: 'OK',
    low: 'LOW',
    high: 'HIGH',
    pipSpread: 'pips spread',
    tradeSl: 'Trade SL',
    initialStop: 'Initial stop',
    hardSl: 'Hard SL',
    maxLoss: 'Max loss',
    pips: 'pips',
    noChasePriceLabel: 'No-Chase Price',
    noChaseDesc: 'Do not enter if price exceeds this level',
    hardLimit: 'HARD LIMIT',
    takeProfitTargets: 'Take Profit Targets',
    levels: 'levels',
    less: 'Less',
    all5: 'All 5',
    timing: 'Timing',
    bestEntryTime: 'Best Entry',
    enterBy: 'Enter By',
    validUntil: 'Valid Until',
    utcOptimal: 'UTC optimal',
    utcLastEntry: 'UTC last entry',
    utcExpires: 'UTC expires',
    rrLabel: 'RR',
    strategy: 'Strategy',
    structure: 'Structure',
    exp: 'Exp',
    livePrice: 'XAU/USD Live Price',
    liveChart: 'XAU/USD · Live Chart',
  },
  es: {
    // Topbar
    brandSub: 'Plataforma de Señales de Oro',
    pairLabel: 'Retroceso',
    live: 'EN VIVO',
    secure: 'SEGURO',
    proMember: 'Miembro Pro',
    howItWorks: 'Cómo Funciona',
    howItWorksSub: 'Guía de señales y trading manual',
    // How It Works Modal
    modalTitle: 'Cómo Funciona el Generador de Señales',
    modalSub: 'XAU ENGINE AI PRO — Guía del Trader',
    section1Title: 'Cómo Funciona el Generador de Señales',
    section1Intro: 'El generador de señales XAU Engine AI analiza continuamente el mercado XAU/USD (Oro) usando un sistema de puntuación multicapa. Cada señal se construye con los siguientes componentes:',
    trendLabel: 'Análisis de Tendencia',
    trendDesc: 'Detecta la dirección dominante del mercado usando medias móviles y estructura de precio en el marco M15.',
    momentumLabel: 'Puntuación de Momentum',
    momentumDesc: 'Mide la fuerza del movimiento actual usando RSI, divergencia MACD y presión de volumen.',
    entryLabel: 'Detección de Zona de Entrada',
    entryDesc: 'Identifica zonas de entrada de alta probabilidad basadas en niveles de soporte/resistencia, retrocesos de Fibonacci y bloques de órdenes.',
    sessionLabel: 'Timing de Sesión',
    sessionDesc: 'Filtra señales por sesiones de trading activas (Londres, Nueva York) para evitar períodos de baja liquidez.',
    riskLabel: 'Filtro de Riesgo',
    riskDesc: 'Suprime señales durante eventos de noticias de alto impacto y condiciones de spread anormales para proteger tu capital.',
    section2Title: 'Tipos de Señales',
    buyDesc: 'Configuración alcista detectada. Se espera que el precio suba.',
    sellDesc: 'Configuración bajista detectada. Se espera que el precio baje.',
    waitDesc: 'Sin configuración clara. Evita operar hasta que las condiciones mejoren.',
    section3Title: 'Cómo Actuar Manualmente en una Señal',
    section3Intro: 'El generador de señales',
    section3IntroNot: 'no',
    section3IntroEnd: 'coloca operaciones automáticamente. Siempre tienes el control. Sigue estos pasos cuando aparezca una señal:',
    step1Title: 'Espera la Confirmación de la Señal',
    step1Desc: 'Una señal solo es accionable cuando el Anillo de Puntuación alcanza ≥ 70%. Por debajo de eso, el motor aún está construyendo confluencia — no entres antes de tiempo.',
    step2Title: 'Verifica la Zona de Entrada',
    step2Desc: 'Mira el panel de Zona de Entrada en la tarjeta de señal. Tu precio de entrada ideal debe estar dentro del rango mostrado. No persigas el precio si ya se ha movido lejos de la zona.',
    step3Title: 'Establece tu Stop Loss',
    step3Desc: 'Usa el nivel de Stop Loss mostrado en la tarjeta de señal. Coloca tu SL unos pips más allá de ese nivel para tener en cuenta el spread. Nunca omitas el stop loss.',
    step4Title: 'Establece tus Objetivos de Take Profit',
    step4Desc: 'La señal proporciona objetivos TP1, TP2 y TP3. Considera cerrar el 50% de tu posición en TP1 y dejar correr el resto hasta TP2/TP3 para maximizar la recompensa.',
    step5Title: 'Monitorea y Gestiona',
    step5Desc: 'Una vez en una operación, observa la puntuación de la señal. Si la puntuación cae bruscamente o aparece una nueva señal opuesta, considera cerrar antes para proteger las ganancias.',
    step6Title: 'Registra tu Operación',
    step6Desc: 'Después de cerrar, revisa la sección de Historial de Señales para comparar tu entrada/salida con los niveles predichos por la señal. Esto te ayuda a mejorar con el tiempo.',
    disclaimer: 'Aviso de Riesgo: ',
    disclaimerText: 'Operar con oro (XAU/USD) implica un riesgo significativo. Las señales son herramientas analíticas, no asesoramiento financiero. Utiliza siempre una gestión de riesgo adecuada y nunca arriesgues más de lo que puedas permitirte perder.',
    // Session bar
    bestWindow: 'Mejor ventana:',
    volatility: 'Volatilidad:',
    overlap: 'SOLAPAMIENTO',
    // Footer
    footerInfo: 'Solo con fines informativos. No es asesoramiento financiero. Gestiona siempre tu propio riesgo.',
    footerVersion: 'XAU Engine AI Pro v2.4.1 — Build 20260325',
    footerPair: 'XAU/USD · M15 · Estrategia Pullback',
    // Signal Generator section
    signalGenerator: 'Generador de Señales',
    signalGeneratorSub: 'Análisis pullback XAU/USD M15 con IA',
    generateSignal: 'Generar Señal',
    generating: 'Generando...',
    refresh: 'Actualizar',
    refreshSignal: 'Actualizar Señal',
    getSignal: 'OBTENER SEÑAL',
    getNewSignal: 'NUEVA SEÑAL',
    tryAgain: 'INTENTAR DE NUEVO',
    signalRequestError: 'Asegúrate de que tu script de fondo Python esté ejecutándose y conectado a Supabase.',
    // Signal history
    signalHistory: 'Historial de Señales',
    signalHistorySub: 'señales',
    totalSignals: 'Total de Señales',
    avgAiScore: 'Puntuación IA Promedio',
    direction: 'Dirección:',
    signalId: 'ID de Señal',
    directionCol: 'Dirección',
    entryZone: 'Zona de Entrada',
    status: 'Estado',
    aiScore: 'Puntuación IA',
    timestamp: 'Fecha/Hora',
    noSignalsYet: 'Aún no hay señales almacenadas',
    noSignalsMatch: 'Ninguna señal coincide con este filtro',
    noSignalsHint: 'Envía POST a /api/signals desde tu bot para almacenar señales',
    adjustFilters: 'Ajusta tus filtros para ver el historial de señales',
    showing: 'Mostrando',
    of: 'de',
    // Signal empty state
    noSignalYet: 'Aún no se ha generado ninguna señal',
    noSignalDesc: 'Presiona',
    noSignalDescMid: 'para ejecutar el análisis pullback de IA en XAU/USD M15. El motor escanea estructura, momentum y contexto de sesión para entregar una configuración de trading de precisión.',
    noSignalRecommended: 'Recomendado: Solo toma señales por encima del 70% en AI Score y Confianza.',
    featureEntryLabel: 'Zonas de Entrada Precisas',
    featureEntryDesc: 'Rangos Mejor + OK con precio sin perseguir',
    featureTpLabel: 'Escalera TP1–TP5',
    featureTpDesc: 'Valores en pips + probabilidad para cada objetivo',
    featureSlLabel: 'Stop Loss Doble',
    featureSlDesc: 'Niveles de SL de operación y SL duro',
    featureTimingLabel: 'Inteligencia de Timing',
    featureTimingDesc: 'Tiempos de mejor entrada, límite de entrada y vencimiento',
    // Signal card labels
    priceAtSignal: 'Precio en señal',
    bestEntry: 'Mejor Entrada',
    okEntry: 'Entrada OK',
    ideal: 'IDEAL',
    ok: 'OK',
    low: 'MÍNIMO',
    high: 'MÁXIMO',
    pipSpread: 'pips de spread',
    tradeSl: 'SL de Operación',
    initialStop: 'Stop inicial',
    hardSl: 'SL Duro',
    maxLoss: 'Pérdida máx.',
    pips: 'pips',
    noChasePriceLabel: 'Precio Sin Perseguir',
    noChaseDesc: 'No entres si el precio supera este nivel',
    hardLimit: 'LÍMITE DURO',
    takeProfitTargets: 'Objetivos de Take Profit',
    levels: 'niveles',
    less: 'Menos',
    all5: 'Todos 5',
    timing: 'Timing',
    bestEntryTime: 'Mejor Entrada',
    enterBy: 'Entrar Antes De',
    validUntil: 'Válido Hasta',
    utcOptimal: 'UTC óptimo',
    utcLastEntry: 'UTC última entrada',
    utcExpires: 'UTC vence',
    rrLabel: 'RR',
    strategy: 'Estrategia',
    structure: 'Estructura',
    exp: 'Vence',
    livePrice: 'Precio en Vivo XAU/USD',
    liveChart: 'XAU/USD · Gráfico en Vivo',
  },
};

type TranslationKeys = keyof typeof translations.en;

interface LanguageContextValue {
  lang: Lang;
  toggleLang: () => void;
  t: (key: TranslationKeys) => string;
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: 'en',
  toggleLang: () => {},
  t: (key) => translations.en[key],
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>('en');

  useEffect(() => {
    const stored = localStorage.getItem('xau-lang') as Lang | null;
    if (stored === 'en' || stored === 'es') setLang(stored);
  }, []);

  useEffect(() => {
    localStorage.setItem('xau-lang', lang);
  }, [lang]);

  const toggleLang = () => setLang((l) => (l === 'en' ? 'es' : 'en'));
  const t = (key: TranslationKeys): string => translations[lang][key] ?? translations.en[key];

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
